import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { DateTime } from 'luxon';
import { type MouseEventHandler, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useAppDispatch } from 'src/app/store/hooks';
import { type NotificationRead } from 'src/common/external/bambi-api/notificationsApi';
import { useTripsRetrieveQuery } from 'src/common/external/bambi-api/tripsApi';
import { formatPassengerFullName } from 'src/common/util/formatFullName';
import { removeUnitedStatesFromAddress } from 'src/common/util/removeUnitedStatesFromAddress';
import { clearFilters, setTripSearchTerm } from 'src/features/trip/trip.slice';

export function TripStatusNotification({
  notification,
}: {
  notification: NotificationRead;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { data: trip, isLoading } = useTripsRetrieveQuery(
    {
      id: notification.ref_entity_id || '',
    },
    { skip: !notification.ref_entity_id }
  );

  const handleViewTrip = useCallback<MouseEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!trip) return;

      dispatch(clearFilters());
      dispatch(setTripSearchTerm(trip.id));

      navigate(`/trips`);
    },
    [trip, navigate, dispatch]
  );

  if (!notification.ref_entity_id) {
    return <div className="text-sm">Trip not found</div>;
  }

  if (isLoading) {
    return <LoadingIndicator variant="small" />;
  }

  if (!trip) {
    return <div className="text-sm">Trip not found</div>;
  }

  return (
    <div className="flex flex-col text-sm">
      <div>
        <strong>Who: </strong>
        {formatPassengerFullName(trip.passenger)}
      </div>
      <div>
        <strong>Pickup Time: </strong>
        {DateTime.fromISO(trip.scheduled_pickup_at).toLocaleString(
          DateTime.DATETIME_MED
        )}
      </div>
      <div>
        <strong>Pickup: </strong>
        {removeUnitedStatesFromAddress(trip.pickup.address)}
      </div>
      <div>
        <strong>Dropoff: </strong>
        {removeUnitedStatesFromAddress(trip.dropoff.address)}
      </div>
      <Button
        variant="anchor-inline"
        justifyContent="start"
        onClick={handleViewTrip}
      >
        View on Trips
      </Button>
    </div>
  );
}
